import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SearchIcon from '@mui/icons-material/Search';
import axios from '../axiosConfig';
import ChatMessageAttachments from '../Components/Chatbot/ChatMessageAttachments';

const FAQ_HASH_PREFIX = '#faq-';
const STATIC_FAQ_NUMBERS = [1, 2, 3, 4, 5, 6];
// Name the API uses for published FAQs without a category (see PublicFaqService).
const GENERAL_CATEGORY_NAMES = ['عام', 'General'];
const SKELETON_ROWS = [0, 1, 2, 3, 4];
const ITEM_DIVIDER = '1px solid #eceff3';
const SCROLL_DELAY_MS = 120;

const visuallyHiddenSx = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  p: 0,
  m: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

// Tashkeel, Quranic annotation marks and tatweel are ignored when searching.
const ARABIC_MARKS_PATTERN = /[\u0610-\u061A\u064B-\u065F\u0670\u06D6-\u06DC\u06DF-\u06E8\u06EA-\u06ED\u0640]/g;

// Arabic-friendly search text: case-insensitive, no tashkeel, أ/إ/آ → ا, ة → ه, ى → ي, single spaces.
const normalizeSearchText = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(ARABIC_MARKS_PATTERN, '')
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/ى/g, 'ي')
    .replace(/\s+/g, ' ')
    .trim();

const asText = (value) => (typeof value === 'string' ? value.trim() : '');

const buildItem = ({ key, question, answer, language = 'ar', alternativeQuestions = [], ...details }) => ({
  key,
  language,
  question,
  answer,
  alternativeQuestions,
  subCategory: '',
  departmentName: '',
  serviceName: '',
  lastReviewedAt: null,
  attachments: [],
  links: [],
  ...details,
  searchText: normalizeSearchText([question, ...alternativeQuestions, answer].join(' ')),
});

// GET /api/Faq dataItem → [{ key, name, language, items }]; incomplete entries are skipped.
const toApiSections = (page) => {
  const language = page?.language === 'en' ? 'en' : 'ar';
  return (Array.isArray(page?.categories) ? page.categories : [])
    .map((category, index) => ({
      key: `category-${index}`,
      name: asText(category?.name),
      language,
      items: (Array.isArray(category?.items) ? category.items : [])
        .filter((item) => item && item.id !== undefined && item.id !== null && asText(item.question) && asText(item.answer))
        .map((item) =>
          buildItem({
            key: String(item.id),
            language,
            question: asText(item.question),
            answer: asText(item.answer),
            alternativeQuestions: (Array.isArray(item.alternativeQuestions) ? item.alternativeQuestions : [])
              .map(asText)
              .filter(Boolean),
            subCategory: asText(item.subCategory),
            departmentName: asText(item.departmentName),
            serviceName: asText(item.serviceName),
            lastReviewedAt: item.lastReviewedAt || null,
            attachments: Array.isArray(item.attachments) ? item.attachments : [],
            links: Array.isArray(item.links) ? item.links : [],
          })
        ),
    }))
    .filter((section) => section.items.length > 0);
};

const readFaqKeyFromHash = (hash) => {
  if (typeof hash !== 'string' || !hash.startsWith(FAQ_HASH_PREFIX)) {
    return null;
  }
  try {
    return decodeURIComponent(hash.slice(FAQ_HASH_PREFIX.length)) || null;
  } catch {
    return null;
  }
};

// replaceState keeps react-router's history state and never adds a history entry.
const replaceLocationHash = (hash) => {
  const { pathname, search, hash: currentHash } = window.location;
  if (currentHash === hash) {
    return;
  }
  try {
    window.history.replaceState(window.history.state, '', `${pathname}${search}${hash}`);
  } catch {
    // History updates can be refused (e.g. sandboxed frames); the page keeps working without the hash.
  }
};

const formatReviewDate = (value, language) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  try {
    return date.toLocaleDateString(language === 'ar' ? 'ar-u-nu-latn' : 'en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '';
  }
};

const FaqItemMeta = ({ item, uiLanguage }) => {
  const { t } = useTranslation();
  const reviewedOn = formatReviewDate(item.lastReviewedAt, uiLanguage);
  const parts = [
    item.subCategory && { key: 'subCategory', label: '', value: item.subCategory },
    item.departmentName && { key: 'department', label: t('help_department'), value: item.departmentName },
    item.serviceName && { key: 'service', label: t('help_service'), value: item.serviceName },
    reviewedOn && { key: 'reviewed', label: t('help_lastReviewed'), value: reviewedOn },
  ].filter(Boolean);

  if (parts.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.5, mt: 1.5 }}>
      {parts.map((part) => (
        <Typography key={part.key} component="span" variant="caption" color="text.secondary">
          {part.label ? `${part.label}: ` : ''}
          <bdi>{part.value}</bdi>
        </Typography>
      ))}
    </Box>
  );
};

const FaqAccordion = ({ item, expanded, isLast, onToggle, uiLanguage, onInternalLinkClick }) => {
  const hasMediaOrLinks = item.attachments.length > 0 || item.links.length > 0;
  const contentLanguage = item.language === 'en' ? 'en' : 'ar';
  const contentDir = contentLanguage === 'ar' ? 'rtl' : 'ltr';

  return (
    <Accordion
      id={`faq-${item.key}`}
      expanded={expanded}
      onChange={(event, isExpanded) => onToggle(item.key, isExpanded)}
      disableGutters
      TransitionProps={{ unmountOnExit: true }}
      sx={{
        '&:before': { display: 'none' },
        borderBottom: isLast ? 'none' : ITEM_DIVIDER,
        boxShadow: 'none',
        scrollMarginTop: { xs: 72, md: 96 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        id={`faq-${item.key}-header`}
        aria-controls={`faq-${item.key}-content`}
      >
        <Typography sx={{ fontWeight: 600 }}>
          <bdi lang={contentLanguage}>{item.question}</bdi>
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Typography
          color="text.secondary"
          dir={contentDir}
          lang={contentLanguage}
          sx={{ whiteSpace: 'pre-line', textAlign: 'start' }}
        >
          {item.answer}
        </Typography>
        <FaqItemMeta item={item} uiLanguage={uiLanguage} />
        {hasMediaOrLinks && (
          <Box sx={{ mt: 1.5 }}>
            <ChatMessageAttachments
              attachments={item.attachments}
              links={item.links}
              onInternalLinkClick={onInternalLinkClick}
            />
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const HelpCenter = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isRTL = i18n.language?.startsWith('ar');
  const uiLanguage = i18n.language?.startsWith('en') ? 'en' : 'ar';

  const [faqRequest, setFaqRequest] = useState({ status: 'loading', page: null });
  const [reloadCount, setReloadCount] = useState(0);
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const [scrollTarget, setScrollTarget] = useState(null);
  const handledNavigationRef = useRef(null);

  // (Re)load on every UI language change. The axios wrapper sends the "lang" header from the language i18next persists
  // (localStorage "i18nextLng"), which is already updated when the languageChanged re-render happens.
  useEffect(() => {
    let active = true;
    setFaqRequest({ status: 'loading', page: null });
    setActiveCategory(null);

    axios
      .get('/Faq')
      .then((page) => {
        if (active) {
          setFaqRequest({ status: 'ready', page });
        }
      })
      .catch(() => {
        if (active) {
          setFaqRequest({ status: 'error', page: null });
        }
      });

    return () => {
      active = false;
    };
  }, [uiLanguage, reloadCount]);

  // Built-in questions about using the portal. They always form the General section (merged with published FAQs
  // that have no category), so they stay visible next to published categories and when nothing is published.
  const generalItems = useMemo(
    () =>
      STATIC_FAQ_NUMBERS.map((number) =>
        buildItem({
          key: `static-${number}`,
          language: uiLanguage,
          question: t(`help_q${number}_question`),
          answer: t(`help_q${number}_answer`),
        })
      ),
    [t, uiLanguage]
  );

  const apiSections = useMemo(() => toApiSections(faqRequest.page), [faqRequest.page]);
  const isLoading = faqRequest.status === 'loading';
  const generalName = t('help_generalCategory');
  const sections = useMemo(() => {
    if (isLoading) {
      return [];
    }
    const generalNames = new Set([...GENERAL_CATEGORY_NAMES, generalName].map(normalizeSearchText));
    const isGeneral = (section) => generalNames.has(normalizeSearchText(section.name));
    return [
      ...apiSections.filter((section) => !isGeneral(section)),
      {
        key: 'general',
        name: generalName,
        language: uiLanguage,
        items: [...generalItems, ...apiSections.filter(isGeneral).flatMap((section) => section.items)],
      },
    ];
  }, [isLoading, apiSections, generalItems, generalName, uiLanguage]);
  const showArabicOnlyNotice =
    !isLoading && apiSections.length > 0 && uiLanguage === 'en' && faqRequest.page?.isFallbackLanguage === true;
  const showSectionHeadings = sections.length > 1;
  const showCategoryChips = sections.length > 1;

  const normalizedQuery = normalizeSearchText(query);
  const visibleSections = useMemo(() => {
    const tokens = normalizedQuery ? normalizedQuery.split(' ') : [];
    return sections
      .filter((section) => activeCategory === null || section.key === activeCategory)
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => tokens.every((token) => item.searchText.includes(token))),
      }))
      .filter((section) => section.items.length > 0);
  }, [sections, activeCategory, normalizedQuery]);

  // Deep link /help#faq-{id}: once per navigation, reveal, expand and scroll to the entry.
  useEffect(() => {
    if (faqRequest.status === 'loading') {
      return;
    }

    const navigationKey = `${location.key}${location.hash}`;
    if (handledNavigationRef.current === navigationKey) {
      return;
    }

    const key = readFaqKeyFromHash(location.hash);
    const found = Boolean(key) && sections.some((section) => section.items.some((item) => item.key === key));
    // After a failed load keep the link pending so a successful retry can still open it.
    if (found || faqRequest.status !== 'error') {
      handledNavigationRef.current = navigationKey;
    }
    if (!found) {
      return;
    }

    setQuery('');
    setActiveCategory(null);
    setExpandedKeys((current) => new Set(current).add(key));
    setScrollTarget(key);
  }, [faqRequest.status, location.key, location.hash, sections]);

  useEffect(() => {
    if (!scrollTarget) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      const element = document.getElementById(`faq-${scrollTarget}`);
      if (element && typeof element.scrollIntoView === 'function') {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      setScrollTarget(null);
    }, SCROLL_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [scrollTarget]);

  const handleToggle = (key, isExpanded) => {
    setExpandedKeys((current) => {
      const next = new Set(current);
      if (isExpanded) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });

    if (isExpanded) {
      replaceLocationHash(`${FAQ_HASH_PREFIX}${encodeURIComponent(key)}`);
    } else if (readFaqKeyFromHash(window.location.hash) === key) {
      replaceLocationHash('');
    }
  };

  const clearFilters = () => {
    setQuery('');
    setActiveCategory(null);
  };

  let faqList;
  if (isLoading) {
    faqList = (
      <Paper elevation={2} sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2 }} aria-busy="true">
        <Box role="status" sx={visuallyHiddenSx}>
          {t('help_loading')}
        </Box>
        {SKELETON_ROWS.map((row) => (
          <Box
            key={row}
            sx={{ py: 1.5, px: 1, borderBottom: row < SKELETON_ROWS.length - 1 ? ITEM_DIVIDER : 'none' }}
          >
            <Skeleton variant="text" sx={{ fontSize: '1.1rem', width: `${85 - row * 8}%` }} />
            <Skeleton variant="text" sx={{ fontSize: '0.8rem', width: '35%' }} />
          </Box>
        ))}
      </Paper>
    );
  } else if (visibleSections.length === 0) {
    faqList = (
      <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('help_noResults')}</Typography>
        <Button size="small" sx={{ mt: 1 }} onClick={clearFilters}>
          {t('help_clearFilters')}
        </Button>
      </Paper>
    );
  } else {
    faqList = visibleSections.map((section) => (
      <Box key={section.key} component="section" sx={{ mb: 2 }}>
        {showSectionHeadings && section.name && (
          <Typography
            variant="subtitle1"
            component="h2"
            color="primary.main"
            sx={{ fontWeight: 700, mb: 1, px: 0.5 }}
          >
            <bdi lang={section.language}>{section.name}</bdi>
          </Typography>
        )}
        <Paper elevation={2} sx={{ p: { xs: 1, md: 2 }, borderRadius: 2 }}>
          {section.items.map((item, index) => (
            <FaqAccordion
              key={item.key}
              item={item}
              expanded={expandedKeys.has(item.key)}
              isLast={index === section.items.length - 1}
              onToggle={handleToggle}
              uiLanguage={uiLanguage}
              onInternalLinkClick={navigate}
            />
          ))}
        </Paper>
      </Box>
    ));
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, md: 4 } }} dir={isRTL ? 'rtl' : 'ltr'}>
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 2,
          mb: 2,
          background: 'linear-gradient(135deg, rgba(188,31,65,0.08) 0%, rgba(188,31,65,0.02) 100%)',
          border: '1px solid rgba(188,31,65,0.16)',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <HelpOutlineIcon color="primary" />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }} color="primary.main">
              {t('help_title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('help_subtitle')}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {showArabicOnlyNotice && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          {t('help_arabicOnlyNotice')}
        </Alert>
      )}

      {faqRequest.status === 'error' && (
        <Alert
          severity="warning"
          sx={{ mb: 2, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setReloadCount((count) => count + 1)}>
              {t('help_retry')}
            </Button>
          }
        >
          {t('help_loadError')}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('help_searchPlaceholder')}
          disabled={isLoading}
          inputProps={{ 'aria-label': t('help_searchLabel'), enterKeyHint: 'search' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton size="small" edge="end" onClick={() => setQuery('')} aria-label={t('help_clearSearch')}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />

        {showCategoryChips && (
          <Box
            role="group"
            aria-label={t('help_categoriesLabel')}
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}
          >
            <Chip
              label={t('help_allCategories')}
              clickable
              color={activeCategory === null ? 'primary' : 'default'}
              variant={activeCategory === null ? 'filled' : 'outlined'}
              onClick={() => setActiveCategory(null)}
              aria-pressed={activeCategory === null}
            />
            {sections.map((section) => {
              const selected = activeCategory === section.key;
              return (
                <Chip
                  key={section.key}
                  label={
                    <>
                      <bdi lang={section.language}>{section.name}</bdi>
                      {` (${section.items.length})`}
                    </>
                  }
                  clickable
                  color={selected ? 'primary' : 'default'}
                  variant={selected ? 'filled' : 'outlined'}
                  onClick={() => setActiveCategory(selected ? null : section.key)}
                  aria-pressed={selected}
                  sx={{ maxWidth: '100%' }}
                />
              );
            })}
          </Box>
        )}
      </Paper>

      {faqList}
    </Container>
  );
};

export default HelpCenter;
